import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, AlertTriangle, Clock, User, Building2,
  MessageSquare, Lock, Send, TrendingUp, BookOpen, ArrowUpRight, Hash,
} from 'lucide-react';
import RelatedLists from '../../../components/RelatedLists';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'pending_customer', label: 'Pending Customer' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];
const PRIORITIES = [
  { value: 1, label: 'P1 — Critical' },
  { value: 2, label: 'P2 — High' },
  { value: 3, label: 'P3 — Moderate' },
  { value: 4, label: 'P4 — Low' },
];

export default function CsmTicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [queues, setQueues] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [commentBody, setCommentBody] = useState('');
  const [commentInternal, setCommentInternal] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/csm/tickets/${id}`, { headers: authHeader() });
      setTicket(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load ticket');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => {
    load();
    axios.get(`${API_URL}/api/csm/queues`, { headers: authHeader() }).then(r => setQueues(r.data.queues || []));
    axios.get(`${API_URL}/api/csm/agents`, { headers: authHeader() }).then(r => setAgents(r.data.agents || []));
  }, [load]);

  const patch = async (updates) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API_URL}/api/csm/tickets/${id}`, updates, { headers: authHeader() });
      setTicket(prev => ({ ...prev, ...r.data, comments: prev.comments, activity: prev.activity, kb_articles: prev.kb_articles }));
      toast.success('Ticket updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const submitComment = async () => {
    if (!commentBody.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/csm/tickets/${id}/comments`,
                       { body: commentBody, is_internal: commentInternal },
                       { headers: authHeader() });
      setCommentBody('');
      setCommentInternal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not post comment');
    } finally { setBusy(false); }
  };

  const escalate = async () => {
    if (!window.confirm('Escalate this ticket? Priority will bump up one level and SLA timers will reset.')) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/csm/tickets/${id}/escalate`, {}, { headers: authHeader() });
      toast.success('Ticket escalated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Escalation failed');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>;
  if (!ticket) return <div className="p-12 text-center text-[#64748B]">Ticket not found</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="csm-ticket-detail">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-[#0E7490] hover:underline">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={escalate} disabled={busy} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="escalate-btn">
            <TrendingUp className="w-4 h-4 mr-1" /> Escalate
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        {/* Main column */}
        <div className="space-y-4">
          <Card className="p-5 border-[#E2E8F0]">
            <div className="flex items-center gap-2 text-xs text-[#64748B] mb-1">
              <Hash className="w-3 h-3" /> <span className="font-mono">{ticket.ticket_number}</span>
              <span>·</span>
              <span>{new Date(ticket.created_at).toLocaleString()}</span>
            </div>
            <h1 className="text-2xl font-bold text-[#0A2342]" data-testid="ticket-title">{ticket.subject || ticket.title}</h1>
            <p className="text-sm text-[#475569] mt-3 whitespace-pre-wrap">{ticket.description}</p>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {ticket.sla_response_breached && (
                <Badge className="bg-rose-100 text-rose-700 border-rose-200"><AlertTriangle className="w-3 h-3 mr-1 inline" /> Response SLA breached</Badge>
              )}
              {ticket.sla_resolution_breached && (
                <Badge className="bg-rose-100 text-rose-700 border-rose-200"><AlertTriangle className="w-3 h-3 mr-1 inline" /> Resolution SLA breached</Badge>
              )}
              {(ticket.tags || []).map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
              {ticket.escalation_level > 0 && <Badge className="bg-orange-100 text-orange-700 border-orange-200">Escalation L{ticket.escalation_level}</Badge>}
            </div>
          </Card>

          {/* Conversation */}
          <Card className="border-[#E2E8F0]">
            <div className="p-4 border-b border-[#E2E8F0] font-semibold text-[#0A2342] flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#0E7490]" /> Conversation
              <span className="text-xs font-normal text-[#64748B]">({(ticket.comments || []).length})</span>
            </div>
            <div className="divide-y divide-[#F1F5F9]">
              {(ticket.comments || []).length === 0 && <div className="p-6 text-center text-sm text-[#94A3B8]">No comments yet — be the first to reply.</div>}
              {(ticket.comments || []).map(c => (
                <div key={c.id} className={`p-4 ${c.is_internal ? 'bg-amber-50' : ''}`} data-testid={`comment-${c.id}`}>
                  <div className="flex items-center gap-2 text-xs text-[#64748B] mb-1">
                    <span className="font-medium text-[#0A2342]">{c.author_name}</span>
                    <span className="text-[10px]">{c.author_role}</span>
                    <span>·</span>
                    <span>{new Date(c.created_at).toLocaleString()}</span>
                    {c.is_internal && <Badge className="bg-amber-200 text-amber-800 border-amber-300 text-[10px] ml-1"><Lock className="w-3 h-3 mr-0.5 inline" /> Internal</Badge>}
                  </div>
                  <p className="text-sm text-[#0A2342] whitespace-pre-wrap">{c.body}</p>
                </div>
              ))}
            </div>
            {/* Compose */}
            <div className="p-4 border-t border-[#E2E8F0] bg-[#F8FAFC]">
              <textarea
                rows={3}
                placeholder="Write a reply…"
                value={commentBody}
                onChange={e => setCommentBody(e.target.value)}
                className="w-full rounded-md border border-[#E2E8F0] px-3 py-2 text-sm bg-white"
                data-testid="comment-body"
              />
              <div className="flex items-center justify-between mt-2">
                <label className="flex items-center gap-2 text-xs text-[#64748B] cursor-pointer">
                  <input type="checkbox" checked={commentInternal} onChange={e => setCommentInternal(e.target.checked)} data-testid="comment-internal" />
                  <Lock className="w-3 h-3" /> Internal note (not visible to customer)
                </label>
                <Button onClick={submitComment} disabled={busy || !commentBody.trim()} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="comment-submit">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Post reply
                </Button>
              </div>
            </div>
          </Card>

          {/* Activity */}
          {(ticket.activity || []).length > 0 && (
            <Card className="p-4 border-[#E2E8F0]">
              <div className="font-semibold text-[#0A2342] mb-3 text-sm">Activity</div>
              <ul className="space-y-2">
                {(ticket.activity || []).map(a => (
                  <li key={a.id} className="text-xs text-[#64748B] flex items-start gap-2">
                    <span className="text-[#94A3B8] mt-0.5">•</span>
                    <span>
                      <strong className="text-[#0A2342]">{a.actor_name}</strong>{' '}
                      <ActivityLabel a={a} />{' '}
                      <span className="text-[#94A3B8]">— {new Date(a.created_at).toLocaleString()}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {/* Related Lists — other tickets from this requester, their assets, linked KBs */}
          <RelatedLists scope="ticket" id={id} />
        </div>

        {/* Side column */}
        <aside className="space-y-4">
          {/* Properties */}
          <Card className="p-4 border-[#E2E8F0]">
            <h3 className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-3">Ticket Properties</h3>
            <Field label="Status">
              <select value={ticket.status} onChange={e => patch({ status: e.target.value })}
                      disabled={busy}
                      className="w-full text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white"
                      data-testid="prop-status">
                {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Priority">
              <select value={ticket.priority || 3} onChange={e => patch({ priority: parseInt(e.target.value, 10) })}
                      disabled={busy}
                      className="w-full text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white"
                      data-testid="prop-priority">
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Queue">
              <select value={ticket.queue_id || ''} onChange={e => patch({ queue_id: e.target.value || null })}
                      disabled={busy}
                      className="w-full text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white"
                      data-testid="prop-queue">
                <option value="">— None —</option>
                {queues.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
              </select>
            </Field>
            <Field label="Assignee">
              <select value={ticket.assigned_agent_id || ''} onChange={e => patch({ assigned_agent_id: e.target.value || null })}
                      disabled={busy}
                      className="w-full text-sm rounded-md border border-[#E2E8F0] px-2 py-1 bg-white"
                      data-testid="prop-assignee">
                <option value="">— Unassigned —</option>
                {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Field>
          </Card>

          {/* SLA */}
          <Card className="p-4 border-[#E2E8F0]">
            <h3 className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-3 flex items-center gap-2">
              <Clock className="w-3 h-3" /> SLA
            </h3>
            <SlaRow
              label="First response"
              minutes={ticket.sla_response_due_minutes_remaining}
              done={!!ticket.first_response_at}
              breached={ticket.sla_response_breached}
            />
            <SlaRow
              label="Resolution"
              minutes={ticket.sla_resolution_due_minutes_remaining}
              done={!!ticket.resolved_at}
              breached={ticket.sla_resolution_breached}
            />
          </Card>

          {/* Customer */}
          <Card className="p-4 border-[#E2E8F0]">
            <h3 className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-3 flex items-center gap-2">
              <User className="w-3 h-3" /> Customer
            </h3>
            <div className="text-sm text-[#0A2342]">{ticket.created_by_name}</div>
            <div className="text-xs text-[#64748B] mt-0.5">{ticket.created_by_role}</div>
            {ticket.organization_name && (
              <div className="text-xs text-[#64748B] mt-2 flex items-center gap-1">
                <Building2 className="w-3 h-3" /> {ticket.organization_name}
              </div>
            )}
          </Card>

          {/* Linked KB */}
          <Card className="p-4 border-[#E2E8F0]">
            <h3 className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-3 flex items-center gap-2">
              <BookOpen className="w-3 h-3" /> Knowledge Articles
            </h3>
            {(ticket.kb_articles || []).length === 0 ? (
              <Link to="/knowledge" className="text-xs text-[#0E7490] hover:underline inline-flex items-center gap-1">
                Browse KB <ArrowUpRight className="w-3 h-3" />
              </Link>
            ) : (
              <ul className="space-y-1">
                {ticket.kb_articles.map(a => (
                  <li key={a.id}>
                    <Link to={`/knowledge/${a.slug || a.id}`} className="text-xs text-[#0E7490] hover:underline inline-flex items-center gap-1">
                      {a.title} <ArrowUpRight className="w-3 h-3" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] text-[#94A3B8] mb-1">{label}</div>
      {children}
    </div>
  );
}

function SlaRow({ label, minutes, done, breached }) {
  const isBreached = breached && !done;
  const hrs = typeof minutes === 'number' ? Math.floor(Math.abs(minutes) / 60) : 0;
  const mins = typeof minutes === 'number' ? Math.abs(minutes) % 60 : 0;
  return (
    <div className="flex items-center justify-between text-xs mb-1.5">
      <span className="text-[#64748B]">{label}</span>
      {done ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">Met</Badge>
        : isBreached ? <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">Breached</Badge>
          : typeof minutes === 'number' ? <span className={minutes < 60 ? 'text-orange-600 font-semibold' : 'text-[#0A2342]'}>{hrs > 0 ? `${hrs}h ` : ''}{mins}m left</span>
            : <span className="text-[#94A3B8]">—</span>}
    </div>
  );
}

function ActivityLabel({ a }) {
  switch (a.action) {
    case 'status_changed': return <>changed status <strong>{a.from}</strong> → <strong>{a.to}</strong></>;
    case 'priority_changed': return <>changed priority <strong>P{a.from}</strong> → <strong>P{a.to}</strong></>;
    case 'assigned': return <>reassigned ticket</>;
    case 'queue_changed': return <>moved queue</>;
    case 'escalated': return <>escalated to level <strong>L{a.to_level}</strong> (P{a.new_priority})</>;
    default: return <>{a.action}</>;
  }
}
